import { createContext, useContext, useMemo, ReactNode } from 'react';

import {
  TCardTransactionColumnConfig,
  TCardTransactionColumnKey,
} from '@/features/cards/store/models/card-transactions.model';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface IColumnsContext {
  columnsConfig: TCardTransactionColumnConfig;
  updateColumnConfig: (newConfig: TCardTransactionColumnConfig) => void;
  activeHeaders: TCardTransactionColumnKey[];
  isAllTransactions?: boolean;
}

const ColumnsContext = createContext<IColumnsContext | undefined>(undefined);

interface IColumnsProviderProps {
  children: ReactNode;
  initialConfig: TCardTransactionColumnConfig;
  isAllTransactions?: boolean;
}

export function ColumnsProvider({ children, initialConfig, isAllTransactions }: IColumnsProviderProps) {
  const [columnsConfig, setColumnsConfig] = useLocalStorage<TCardTransactionColumnConfig>(
    isAllTransactions ? 'allCardTransactionColumnConfig' : 'userCardTransactionColumnConfig', // key for localStorage
    initialConfig,
  );

  const activeHeaders = useMemo(() => {
    return Object.entries(columnsConfig)
      .filter(([_, config]) => {
        return config.isActive;
      })
      .map(([key]) => key as TCardTransactionColumnKey);
  }, [columnsConfig]);

  const value = {
    columnsConfig,
    updateColumnConfig: setColumnsConfig,
    activeHeaders,
    isAllTransactions,
  };

  return <ColumnsContext.Provider value={value}>{children}</ColumnsContext.Provider>;
}

export function useColumns() {
  const context = useContext(ColumnsContext);
  if (!context) {
    throw new Error('useColumns must be used within a ColumnsProvider');
  }
  return context;
}
